import { atom } from "jotai";
import {
  chatbotUsageAtom_SUSPENDS,
  interfacesUsageAtom_SUSPENDS,
  TablesApiResponse,
  tablesUsageAtom_SUSPENDS,
  InterfacesApiResponse,
  ChatbotUsageSuccessResponse,
} from "../apps";
import {
  numberOfUniqueChatbotNodesAtom,
  numberOfUniqueInterfaceNodesAtom,
  numberOfUniqueTableNodesAtom,
} from "../nodes";
import { getUpgradePlanType } from "../utils";
import { PlanType } from "../types";
import { isTemplatePageAtom } from "./isTemplatePageAtom";

interface UpgradeBannerProps {
  title: string;
  description: string;
  tablesPlanType?: PlanType;
  interfacesPlanType?: PlanType;
  chatbotsPlanType?: PlanType;
}

const getRemainingTableCapacity = (usage: TablesApiResponse) =>
  Math.max(0, usage.maxTableCount - usage.tableCount);

const getRemainingInterfaceCapacity = (usage: InterfacesApiResponse) =>
  Math.max(
    0,
    usage.currentInterfacesAccountPageLimit -
      usage.currentInterfacesAccountPageCount,
  );

const getRemainingChatbotCapacity = (usage: ChatbotUsageSuccessResponse) =>
  usage.chatbots.limit === null
    ? Infinity
    : Math.max(0, usage.chatbots.limit - usage.chatbots.count);

export const oldUpgradeBannerPropsAtom_SUSPENDS = atom<
  Promise<UpgradeBannerProps | null>
>(async (get) => {
  const numberOfTableNodes = get(numberOfUniqueTableNodesAtom);
  const numberOfInterfaceNodes = get(numberOfUniqueInterfaceNodesAtom);
  const numberOfChatbotNodes = get(numberOfUniqueChatbotNodesAtom);

  const results = await Promise.allSettled([
    get(tablesUsageAtom_SUSPENDS),
    get(interfacesUsageAtom_SUSPENDS),
    get(chatbotUsageAtom_SUSPENDS),
  ]);

  const [tablesUsage, interfacesUsage, chatbotsUsage] = results.map((result) =>
    result.status === "fulfilled" ? result.value : null,
  ) as [
    TablesApiResponse | null,
    InterfacesApiResponse | null,
    ChatbotUsageSuccessResponse | null,
  ];

  const isTemplatePage = get(isTemplatePageAtom);

  const systemOrTemplate = isTemplatePage ? "template" : "system";

  // Tables
  const needsToUpgradeTables = tablesUsage
    ? numberOfTableNodes > getRemainingTableCapacity(tablesUsage)
    : false;

  const tablesPlanType =
    needsToUpgradeTables && tablesUsage
      ? getUpgradePlanType({
          type: "tables",
          currentPlanLimit: tablesUsage.maxTableCount,
        })
      : undefined;

  // Interfaces
  const needsToUpgradeInterfaces = interfacesUsage
    ? numberOfInterfaceNodes > getRemainingInterfaceCapacity(interfacesUsage)
    : false;

  const interfacesPlanType =
    needsToUpgradeInterfaces && interfacesUsage
      ? getUpgradePlanType({
          type: "interfaces",
          currentPlanName: interfacesUsage.currentInterfacesPlanName,
        })
      : undefined;

  // Chatbots
  const needsToUpgradeChatbots = chatbotsUsage
    ? numberOfChatbotNodes > getRemainingChatbotCapacity(chatbotsUsage)
    : false;

  const chatbotsPlanType =
    needsToUpgradeChatbots && chatbotsUsage
      ? getUpgradePlanType({
          type: "chatbots",
          currentPlanName: chatbotsUsage.plan.name,
        })
      : undefined;

  // Single upgrades first - more specific messages
  if (
    needsToUpgradeTables &&
    !needsToUpgradeInterfaces &&
    !needsToUpgradeChatbots
  ) {
    return {
      title: "You have reached your tables limit",
      description: `${
        numberOfTableNodes === 1
          ? "A new table"
          : `${numberOfTableNodes} tables`
      } will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      tablesPlanType,
    };
  }

  if (
    needsToUpgradeInterfaces &&
    !needsToUpgradeTables &&
    !needsToUpgradeChatbots
  ) {
    return {
      title: "You have reached your interfaces limit",
      description: `${
        numberOfInterfaceNodes === 1
          ? "A new interface"
          : `${numberOfInterfaceNodes} interfaces`
      } will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      interfacesPlanType,
    };
  }

  if (
    needsToUpgradeChatbots &&
    !needsToUpgradeTables &&
    !needsToUpgradeInterfaces
  ) {
    return {
      title: "You have reached your chatbots limit",
      description: `${
        numberOfChatbotNodes === 1
          ? "A new chatbot"
          : `${numberOfChatbotNodes} chatbots`
      } will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      chatbotsPlanType,
    };
  }

  // Multiple upgrades - generic message with appropriate plan types
  if (tablesPlanType || interfacesPlanType || chatbotsPlanType) {
    return {
      title: `Upgrade to build this ${systemOrTemplate}`,
      description: `New assets will be created that will put you over your plans limit. Upgrade to use this ${systemOrTemplate}.`,
      tablesPlanType,
      interfacesPlanType,
      chatbotsPlanType,
    };
  }

  return null;
});

oldUpgradeBannerPropsAtom_SUSPENDS.debugLabel =
  "oldUpgradeBannerPropsAtom_SUSPENDS";
